import { RadioQuestion, SliderQuestion, CheckboxQuestion, HeaderQuestion, CompletionQuestion } from '../questionTypes';

// Type alias for all question types used in Snapshot assessment
type SnapshotQuestion = RadioQuestion | SliderQuestion | CheckboxQuestion | HeaderQuestion;

export const snapshotAssessmentQuestions: SnapshotQuestion[] = [
  // SECTION 1: PLANNING STATUS
  {
    id: "section-1-header",
    type: "header",
    title: "Planning Status",
    description: "Let's understand where you are in your succession planning journey.",
    required: false,
  },
  {
    id: "planning_status_current",
    type: "radio",
    title: "How would you describe your current succession planning?",
    options: [
      { value: "comprehensive", label: "Comprehensive: I have a formal, written plan" },
      { value: "active", label: "Active: I'm actively working on a plan" },
      { value: "early_stage", label: "Early stage: I'm thinking seriously about it" },
      { value: "awareness", label: "Awareness: I know it's important but haven't started" },
      { value: "not_focused", label: "Not yet focused: It's a future consideration" }
    ],
    section: "Planning Status",
    required: true,
  },
  {
    id: "succession_trigger_primary",
    type: "radio",
    title: "What's prompting you to explore succession planning now?",
    options: [
      { value: "health_concerns", label: "Health concerns" },
      { value: "retirement_planning", label: "Retirement planning" },
      { value: "market_opportunity", label: "Market opportunity" },
      { value: "burnout", label: "Burnout" },
      { value: "family_pressure", label: "Family pressure" },
      { value: "financial_needs", label: "Financial needs" },
      { value: "strategic_timing", label: "Strategic timing" },
      { value: "other", label: "Other" }
    ],
    section: "Planning Status",
    required: true,
  },

  // SECTION 2: BUSINESS PROFILE
  {
    id: "section-2-header",
    type: "header",
    title: "Business Profile",
    description: "Tell us about your practice to help us understand your situation.",
    required: false,
  },
  {
    id: "years_in_operation",
    type: "radio",
    title: "How long have you been operating your advisory practice?",
    options: [
      { value: "under_2", label: "Less than 2 years" },
      { value: "2_to_5", label: "2-5 years" },
      { value: "5_to_10", label: "5-10 years" },
      { value: "10_to_15", label: "10-15 years" },
      { value: "15_to_20", label: "15-20 years" },
      { value: "over_20", label: "20+ years" }
    ],
    section: "Business Profile",
    required: true,
  },
  {
    id: "regulatory_framework",
    type: "checkbox",
    title: "Which regulatory framework governs your practice?",
    options: [
      { value: "mfda", label: "MFDA" },
      { value: "iiroc", label: "IIROC" },
      { value: "provincial_insurance", label: "Provincial insurance" },
      { value: "other", label: "Other" }
    ],
    section: "Business Profile",
    required: true,
  },
  {
    id: "revenue_range",
    type: "radio",
    title: "What is your annual recurring revenue?",
    options: [
      { value: "under_250k", label: "Under $250,000" },
      { value: "250k_to_500k", label: "$250,000 - $500,000" },
      { value: "500k_to_1m", label: "$500,000 - $1,000,000" },
      { value: "1m_to_2m", label: "$1,000,000 - $2,000,000" },
      { value: "2m_to_5m", label: "$2,000,000 - $5,000,000" },
      { value: "over_5m", label: "Over $5,000,000" }
    ],
    section: "Business Profile",
    required: true,
  },
  {
    id: "aum_range",
    type: "radio",
    title: "What is your total assets under management (AUM)?",
    options: [
      { value: "under_25m", label: "Under $25 million" },
      { value: "25m_to_50m", label: "$25 - $50 million" },
      { value: "50m_to_100m", label: "$50 - $100 million" },
      { value: "100m_to_250m", label: "$100 - $250 million" },
      { value: "250m_to_500m", label: "$250 - $500 million" },
      { value: "over_500m", label: "Over $500 million" }
    ],
    section: "Business Profile",
    required: true,
  },
  {
    id: "client_count_range",
    type: "radio",
    title: "How many client households do you serve?",
    options: [
      { value: "under_100", label: "Under 100" },
      { value: "100_to_250", label: "100 - 250" },
      { value: "250_to_500", label: "250 - 500" },
      { value: "500_to_1000", label: "500 - 1,000" },
      { value: "over_1000", label: "Over 1,000" }
    ],
    section: "Business Profile",
    required: true,
  },

  // SECTION 3: FINANCIAL METRICS
  {
    id: "section-3-header",
    type: "header",
    title: "Financial Metrics",
    description: "Understanding your financial position helps us provide relevant guidance.",
    required: false,
  },
  {
    id: "recurring_revenue_percentage",
    type: "slider",
    title: "What percentage of your revenue is recurring/predictable?",
    minValue: 0,
    maxValue: 100,
    step: 5,
    defaultValue: 50,
    valueLabel: "%",
    labels: {
      min: "0%",
      max: "100%"
    },
    section: "Financial Metrics",
    required: true,
  },
  {
    id: "owner_valuation_estimate",
    type: "radio",
    title: "In your opinion, what is your best estimate of your practice/book of business value today?",
    options: [
      { value: "under_500k", label: "Under $500,000" },
      { value: "500k_to_1m", label: "$500,000 - $1,000,000" },
      { value: "1m_to_1_5m", label: "$1,000,000 - $1,500,000" },
      { value: "1_5m_to_2m", label: "$1,500,000 - $2,000,000" },
      { value: "over_2m", label: "Over $2,000,000" },
      { value: "no_estimate", label: "I don't have a realistic estimate" }
    ],
    section: "Financial Metrics",
    required: true,
  },
  {
    id: "valuation_confidence_level",
    type: "radio",
    title: "How confident are you in that estimate?",
    options: [
      { value: "very_confident", label: "Very confident: based on formal valuation" },
      { value: "somewhat_confident", label: "Somewhat confident: an educated guess" },
      { value: "not_very_confident", label: "Not very confident: It's a rough estimate" },
      { value: "not_confident", label: "Not confident at all: It's a pure guess" }
    ],
    section: "Financial Metrics",
    required: true,
    conditionalDisplay: {
      dependsOn: "owner_valuation_estimate",
      showWhen: ["under_500k", "500k_to_1m", "1m_to_1_5m", "1_5m_to_2m", "over_2m"]
    }
  },

  // SECTION 4: TEAM & TIMELINE
  {
    id: "section-4-header",
    type: "header",
    title: "Team & Timeline",
    description: "Your team structure and timeline expectations shape your succession options.",
    required: false,
  },
  {
    id: "team_size_total",
    type: "radio",
    title: "Including yourself, how many people work in your practice?",
    options: [
      { value: "solo", label: "Just me" },
      { value: "2_to_3", label: "2-3 people" },
      { value: "4_to_6", label: "4-6 people" },
      { value: "7_to_10", label: "7-10 people" },
      { value: "11_to_20", label: "11-20 people" },
      { value: "over_20", label: "20+ people" }
    ],
    section: "Team & Timeline",
    required: true,
  },
  {
    id: "succession_timeline_years",
    type: "radio",
    title: "When do you ideally want to transition from your practice?",
    options: [
      { value: "under_1_year", label: "Less than 1 year" },
      { value: "1_to_2_years", label: "1-2 years" },
      { value: "3_to_5_years", label: "3-5 years" },
      { value: "5_to_10_years", label: "5-10 years" },
      { value: "over_10_years", label: "10+ years" },
      { value: "unsure", label: "Unsure" }
    ],
    section: "Team & Timeline",
    required: true,
  },
  {
    id: "successor_identified",
    type: "radio",
    title: "Do you have a successor in mind?",
    options: [
      { value: "yes_identified", label: "Yes - identified" },
      { value: "yes_multiple", label: "Yes - multiple options" },
      { value: "considering", label: "Considering someone" },
      { value: "no_need_to_find", label: "No - need to find" },
      { value: "not_sure", label: "Not sure yet" }
    ],
    section: "Team & Timeline",
    required: true,
  },

  // SECTION 5: CONCERNS & READINESS
  {
    id: "section-5-header",
    type: "header",
    title: "Concerns & Readiness",
    description: "Understanding your concerns helps us address what matters most to you.",
    required: false,
  },
  {
    id: "succession_concern_primary",
    type: "radio",
    title: "What concerns you most about succession?",
    options: [
      { value: "finding_buyer", label: "Finding right buyer/successor" },
      { value: "valuation", label: "Business valuation" },
      { value: "tax_implications", label: "Tax implications" },
      { value: "client_retention", label: "Client retention" },
      { value: "employee_welfare", label: "Employee welfare" },
      { value: "personal_readiness", label: "Personal readiness" },
      { value: "timeline_pressure", label: "Timeline pressure" },
      { value: "other", label: "Other" }
    ],
    section: "Concerns & Readiness",
    required: true,
  },
  {
    id: "client_age_average",
    type: "radio",
    title: "What's the average age of your client base?",
    options: [
      { value: "under_40", label: "Under 40" },
      { value: "40_to_50", label: "40-50" },
      { value: "50_to_60", label: "50-60" },
      { value: "60_to_70", label: "60-70" },
      { value: "over_70", label: "Over 70" },
      { value: "mixed", label: "Mixed" }
    ],
    section: "Concerns & Readiness",
    required: true,
  },
  {
    id: "digital_infrastructure_maturity",
    type: "slider",
    title: "Rate your practice's digital infrastructure",
    minValue: 1,
    maxValue: 5,
    step: 1,
    defaultValue: 3,
    valueLabel: "",
    stepLabels: [
      "Paper-based",
      "Mostly paper, some digital",
      "Mix of paper and digital",
      "Mostly digital, some paper",
      "Fully digital"
    ],
    labels: {
      min: "Paper-based",
      max: "Fully digital"
    },
    section: "Concerns & Readiness",
    required: true,
  },
  {
    id: "owner_dependency_percentage",
    type: "slider",
    title: "How dependent is the practice on you personally?",
    minValue: 0,
    maxValue: 100,
    step: 5,
    defaultValue: 50,
    valueLabel: "%",
    labels: {
      min: "0%",
      max: "100%"
    },
    section: "Concerns & Readiness",
    required: true,
  },
  {
    id: "personal_financial_readiness",
    type: "radio",
    title: "Could you maintain your lifestyle if you sold today?",
    options: [
      { value: "yes_definitely", label: "Yes - definitely" },
      { value: "probably", label: "Probably" },
      { value: "unsure", label: "Unsure" },
      { value: "probably_not", label: "Probably not" },
      { value: "definitely_not", label: "Definitely not" }
    ],
    section: "Concerns & Readiness",
    required: true,
  },

  // SECTION 6: FINAL DETAILS
  {
    id: "section-6-header",
    type: "header",
    title: "Final Details",
    description: "A few more details to complete your succession snapshot.",
    required: false,
  },
  {
    id: "cross_border_complexity",
    type: "radio",
    title: "Do you have U.S. residency that might create cross-border tax issues when you sell the practice/book of business?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" }
    ],
    section: "Final Details",
    required: true,
  },
  {
    id: "immediate_support_needs",
    type: "checkbox",
    title: "What would help you most right now?",
    options: [
      { value: "business_valuation", label: "Business valuation" },
      { value: "find_successor", label: "Find successor" },
      { value: "tax_planning", label: "Tax planning" },
      { value: "personal_financial_planning", label: "Personal financial planning" },
      { value: "build_advisory_team", label: "Build advisory team" },
      { value: "reduce_owner_dependency", label: "Reduce owner dependency" },
      { value: "exit_strategy", label: "Exit strategy" },
      { value: "other", label: "Other" }
    ],
    section: "Final Details",
    required: true,
  },
];

// mySuccession Snapshot Completion Question
export const snapshotCompletionQuestion: CompletionQuestion = {
  id: "snapshot-succession-completion",
  type: "completion",
  title: "Thanks for sharing where you are today — we've got a great starting point",
  description: "We're now developing a custom guide for your Next Steps based on your answers and have identified key areas of opportunity we can build together.",
  required: false,
};

// Combined questions including completion
export const snapshotAssessmentQuestionsWithCompletion: (SnapshotQuestion | CompletionQuestion)[] = [
  ...snapshotAssessmentQuestions,
  snapshotCompletionQuestion
];
